import clsx from 'clsx';
import { SessionSummary } from '../../hooks/useProgress';

export interface SimpleScoreChartProps {
  sessions: SessionSummary[];
  height?: number;
  showLabels?: boolean;
  metric?: 'overallScore' | 'clarity' | 'pace' | 'confidence' | 'energy';
  /**
   * The server's canonical average to draw as the reference line (null/absent =
   * not measured: no line). The chart never averages its own points.
   */
  average?: number | null;
}

export function SimpleScoreChart({
  sessions,
  height = 200,
  showLabels = true,
  metric = 'overallScore',
  average = null,
}: SimpleScoreChartProps) {
  if (sessions.length === 0) {
    return (
      <div
        className="flex items-center justify-center bg-gray-50 rounded-lg"
        style={{ height }}
      >
        <p className="text-sm text-gray-400">No session data yet. Start practicing!</p>
      </div>
    );
  }

  const maxScore = 100;

  const maxBarHeight = height - (showLabels ? 40 : 20);

  // The canonical average from the server; nothing measured = no line (never 0).
  const avgScore = typeof average === 'number' && Number.isFinite(average) ? Math.round(average) : null;

  return (
    <div className="w-full">
      {/* Chart area */}
      <div className="relative" style={{ height }}>
        {/* Y-axis labels */}
        <div className="absolute left-0 top-0 bottom-0 w-8 flex flex-col justify-between text-xs text-gray-400 pr-2">
          <span>100</span>
          <span>75</span>
          <span>50</span>
          <span>25</span>
          <span>0</span>
        </div>

        {/* Chart content */}
        <div className="ml-10 h-full relative">
          {/* Grid lines */}
          <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="border-b border-gray-100 w-full" />
            ))}
          </div>

          {/* Average line — only when something was measured */}
          {avgScore !== null && (
            <div
              className="absolute left-0 right-0 border-t-2 border-dashed border-primary-300 z-10"
              style={{ bottom: `${(avgScore / maxScore) * maxBarHeight}px` }}
            >
              <span className="absolute -top-5 right-0 text-xs text-primary-600 bg-white px-1">
                Avg: {avgScore}%
              </span>
            </div>
          )}

          {/* Bars */}
          <div className="h-full flex items-end justify-around gap-2 px-2">
            {sessions.map((session) => {
              const score = session[metric];
              const barHeight = score === null ? 0 : (score / maxScore) * maxBarHeight;
              const date = new Date(session.date);
              const label = `${date.getMonth() + 1}/${date.getDate()}`;

              return (
                <div key={session.id} className="flex flex-col items-center flex-1 max-w-16">
                  {/* Score label on hover */}
                  <div className="relative w-full group">
                    <div
                      className={clsx(
                        'w-full rounded-t-md transition-all cursor-pointer relative',
                        score === null ? 'bg-gray-200' : score >= 80 ? 'bg-green-500' : score >= 60 ? 'bg-primary-500' : 'bg-orange-500'
                      )}
                      style={{ height: `${barHeight}px`, minHeight: '4px' }}
                    >
                      {/* Tooltip */}
                      <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-20">
                        {score === null ? 'Not measured' : `${score}%`}
                      </div>
                    </div>
                  </div>

                  {/* X-axis label */}
                  {showLabels && (
                    <span className="mt-2 text-xs text-gray-500 truncate w-full text-center">
                      {label}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// Line chart alternative
export function SimpleLineChart({
  sessions,
  height = 200,
  metric = 'overallScore',
}: SimpleScoreChartProps) {
  if (sessions.length < 2) {
    return (
      <div
        className="flex items-center justify-center bg-gray-50 rounded-lg"
        style={{ height }}
      >
        <p className="text-sm text-gray-400">Need at least 2 sessions to show trend</p>
      </div>
    );
  }

  const scores = sessions.map((s) => s[metric]);
  const maxScore = 100;
  const padding = 40;
  const chartHeight = height - padding;
  const chartWidth = sessions.length * 60;

  // Generate SVG path points
  // Unmeasured sessions are left out of the line rather than drawn at zero.
  const points = scores.flatMap((score, index) => {
    if (score === null) return [];
    const x = padding + (index * (chartWidth - padding)) / Math.max(1, scores.length - 1);
    const y = chartHeight - (score / maxScore) * (chartHeight - padding) + padding / 2;
    return [{ x, y, score }];
  });

  // Create path string
  const pathD = points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
    .join(' ');

  // Create area fill path
  const areaD = `${pathD} L ${points[points.length - 1].x} ${chartHeight} L ${padding} ${chartHeight} Z`;

  return (
    <div className="w-full overflow-x-auto">
      <svg width={Math.max(chartWidth + padding, 400)} height={height} className="min-w-full">
        {/* Grid lines */}
        {[0, 25, 50, 75, 100].map((val) => {
          const y = chartHeight - (val / maxScore) * (chartHeight - padding) + padding / 2;
          return (
            <g key={val}>
              <line
                x1={padding}
                y1={y}
                x2={chartWidth}
                y2={y}
                stroke="#f3f4f6"
                strokeWidth="1"
              />
              <text x="5" y={y + 4} fontSize="10" fill="#9ca3af">
                {val}
              </text>
            </g>
          );
        })}

        {/* Area fill */}
        <path d={areaD} fill="url(#gradient)" opacity="0.3" />

        {/* Line */}
        <path d={pathD} fill="none" stroke="#6366f1" strokeWidth="3" strokeLinecap="round" />

        {/* Data points */}
        {points.map((point, index) => (
          <g key={index}>
            <circle cx={point.x} cy={point.y} r="6" fill="white" stroke="#6366f1" strokeWidth="2" />
            <text x={point.x} y={point.y - 12} fontSize="10" fill="#6366f1" textAnchor="middle">
              {point.score}%
            </text>
            {/* X-axis label */}
            <text
              x={point.x}
              y={chartHeight + 15}
              fontSize="10"
              fill="#9ca3af"
              textAnchor="middle"
            >
              {new Date(sessions[index].date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </text>
          </g>
        ))}

        {/* Gradient definition */}
        <defs>
          <linearGradient id="gradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#6366f1" />
            <stop offset="100%" stopColor="#6366f1" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
}

// Skills breakdown radar-like display
export interface SkillsBreakdownProps {
  clarity: number | null;
  pace: number | null;
  /** null = not measured (no vocal evidence). Rendered as such, never as 0%. */
  confidence: number | null;
  /** null = not measured (no vocal evidence). Rendered as such, never as 0%. */
  energy: number | null;
}

export function SkillsBreakdown({ clarity, pace, confidence, energy }: SkillsBreakdownProps) {
  const skills: { name: string; value: number | null; target: number; note?: string }[] = [
    { name: 'Clarity', value: clarity, target: 85 },
    { name: 'Pace', value: pace, target: 80 },
    {
      name: 'Confidence',
      value: confidence,
      target: 75,
      note: 'Requires vocal delivery analysis',
    },
    { name: 'Energy', value: energy, target: 80, note: 'Requires vocal delivery analysis' },
  ];

  return (
    <div className="space-y-4">
      {skills.map((skill) =>
        skill.value === null ? (
          <div key={skill.name}>
            <div className="flex justify-between text-sm mb-1">
              <span className="font-medium text-gray-700">{skill.name}</span>
              <span className="text-gray-400 italic">Not measured</span>
            </div>
            {skill.note && <p className="text-xs text-gray-400">{skill.note}</p>}
          </div>
        ) : (
        <div key={skill.name}>
          <div className="flex justify-between text-sm mb-1">
            <span className="font-medium text-gray-700">{skill.name}</span>
            <span
              className={clsx(
                'font-semibold',
                skill.value >= skill.target ? 'text-green-600' : 'text-gray-900'
              )}
            >
              {skill.value}%
              {skill.value >= skill.target && (
                <span className="ml-1 text-green-500">+</span>
              )}
            </span>
          </div>
          <div className="relative h-3 bg-gray-100 rounded-full overflow-hidden">
            {/* Target marker */}
            <div
              className="absolute top-0 bottom-0 w-0.5 bg-gray-400 z-10"
              style={{ left: `${skill.target}%` }}
            />
            {/* Progress */}
            <div
              className={clsx(
                'h-full rounded-full transition-all',
                skill.value >= skill.target ? 'bg-green-500' : skill.value >= 60 ? 'bg-primary-500' : 'bg-orange-500'
              )}
              style={{ width: `${skill.value}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-gray-400 mt-0.5">
            <span>0%</span>
            <span>Target: {skill.target}%</span>
            <span>100%</span>
          </div>
        </div>
        )
      )}
    </div>
  );
}
